package com.zi.support.shared

data class IncidentField(val label: String, val value: String)
data class IncidentSection(val title: String, val fields: List<IncidentField>)

/**
 * Incident detail layout shared by web, Android and iOS: every field of an `incidents` document lands in exactly one
 * section, so nothing the reporting app sent is hidden. Fields this list doesn't know go to "Other fields".
 */
object IncidentFields {
    // Regexes here run on web too: JavaScript rejects inline flags like "(?i)", so use RegexOption instead.
    private val groups = listOf(
        "App" to listOf("appName" to "App", "appVariant" to "Variant", "appVersion" to "Version", "sourcePackage" to "Package"),
        "Device" to listOf(
            "deviceName" to "Device name", "deviceModel" to "Model", "deviceId" to "Device ID",
            "deviceSerial" to "Serial number", "deviceImei" to "IMEI", "operatorName" to "Used by",
        ),
        "Status" to listOf(
            "status" to "Status", "severity" to "Severity", "details.occurredAt" to "Occurred", "timestamp" to "Received",
            "resolvedAt" to "Resolved at", "resolvedBy" to "Resolved by",
        ),
    )
    /** Shown elsewhere on the screen: the error card at the top and the Technical details block. */
    private val shownElsewhere = setOf("errorType", "errorMessage", "stackTrace")

    /**
     * [flat] is every field of the document as display text, nested maps flattened to dotted keys
     * ("details.originalErrorType"). Blank values and empty sections are dropped.
     */
    fun sections(flat: Map<String, String>): List<IncidentSection> = shown(flat + ("severity" to IncidentSeverity.title(
        // Severity shows as the 1-5 level, not the reporting app's own "error"/"warning".
        IncidentSeverity.level(flat["errorType"].orEmpty(), flat["errorMessage"].orEmpty(), flat["severity"].orEmpty()))))

    private fun shown(flat: Map<String, String>): List<IncidentSection> {
        val grouped = groups.flatMap { (_, fields) -> fields.map { it.first } }.toSet() + shownElsewhere
        fun fields(keys: List<Pair<String, String>>) = keys.mapNotNull { (key, label) ->
            // Reporting apps sometimes send the text "null"/"undefined" for a missing value: treat it as blank.
            flat[key]?.takeIf { it.isNotBlank() && it.trim().lowercase() !in setOf("null", "undefined") }?.let {
                IncidentField(label, if (key.substringAfterLast('.').lowercase() in secretKeys) "[REDACTED]" else redact(it))
            }
        }
        val locationKeys = flat.keys.filter { locationField.containsMatchIn(it.substringAfterLast('.')) }.sorted()
        val details = flat.keys.filter { it.startsWith("details.") && it !in locationKeys && it !in grouped }.sorted()
            .map { it to label(it.removePrefix("details.")) }
        val other = flat.keys.filter { it !in grouped && !it.startsWith("details.") && it !in locationKeys }.sorted().map { it to label(it) }
        return (listOf(IncidentSection("Location", fields(locationKeys.map { it to label(it.substringAfterLast('.')) }))) +
            groups.map { (title, keys) -> IncidentSection(title, fields(keys)) } +
            IncidentSection("Details", fields(details)) + IncidentSection("Other fields", fields(other)))
            .filter { it.fields.isNotEmpty() }
    }

    /**
     * The card's two time lines: when the error happened on the phone (`details.occurredAt`) and when the report reached
     * the server (`timestamp`). Both are always shown; older reporting app versions don't send occurredAt, which the card
     * says rather than hiding. Platforms pass the dates already formatted, null when missing.
     */
    /** "KPH Logistics Live • https://kph…:56308" → "KPH Logistics Live": cards show the name, the detail view shows the server. */
    fun companyName(raw: String): String = raw.substringBefore(" • ").substringBefore(" - http").trim()

    /**
     * What issue lists sort by, newest first: when it happened on the phone, so reports a phone sent late (it was
     * offline) still land in the right place. A phone clock ahead of the server, or no occurredAt, falls back to received.
     */
    fun sortMillis(occurredMillis: Long, receivedMillis: Long): Long = when {
        receivedMillis <= 0 -> occurredMillis
        occurredMillis in 1..receivedMillis -> occurredMillis
        else -> receivedMillis
    }

    fun cardTime(occurred: String?, received: String?): String =
        "Occurred ${occurred ?: "– not sent by this app version"}\nReceived ${received ?: "–"}"

    private val locationField = Regex("(latitude|longitude|location|accuracy|mapurl)", RegexOption.IGNORE_CASE)

    /**
     * Google Maps link for where the incident happened, from any "...Latitude" field with a matching "...Longitude"
     * (the driver app sends details.connectionLostLatitude/Longitude), or null when the record has no usable position.
     */
    fun mapsUrl(flat: Map<String, String>): String? {
        for ((key, value) in flat) {
            val longitudeKey = when {
                key.endsWith("Latitude") -> key.dropLast(8) + "Longitude"
                key.endsWith("latitude") -> key.dropLast(8) + "longitude"
                else -> continue
            }
            val latitude = value.trim().toDoubleOrNull() ?: continue
            val longitude = flat[longitudeKey]?.trim()?.toDoubleOrNull() ?: continue
            if (latitude !in -90.0..90.0 || longitude !in -180.0..180.0 || (latitude == 0.0 && longitude == 0.0)) continue
            return "https://www.google.com/maps/search/?api=1&query=$latitude,$longitude"
        }
        return null
    }

    private val secretKeys = setOf("password", "token", "authorization", "apikey", "accesstoken", "refreshtoken")

    /** Masks credentials an app may have included in a message or URL, e.g. "token=abc" -> "token=[REDACTED]". */
    fun redact(value: String): String =
        value.replace(Regex("(password|token|authorization|api[_-]?key)([=: ]+)[^&\\s,}]+", RegexOption.IGNORE_CASE), "$1$2[REDACTED]")

    /** "network.originalErrorType" -> "Network › Original error type". */
    fun label(key: String): String = key.split('.').joinToString(" › ") { part ->
        part.replace(Regex("([a-z0-9])([A-Z])"), "$1 $2").replace('_', ' ').lowercase().replaceFirstChar { it.uppercase() }
    }
}
